import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";

type AdminPageSkeletonProps = {
  variant?: "list" | "detail" | "form";
  kpiCount?: number;
};

export function AdminPageSkeleton({
  variant = "list",
  kpiCount = 4,
}: AdminPageSkeletonProps) {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48 rounded-full" />
          <Skeleton className="h-4 w-72 rounded-full" />
        </div>
        <Skeleton className="h-10 w-32 rounded-full" />
      </div>

      {variant !== "form" && (
        <div className={cn("grid w-full gap-3", kpiCount > 4 ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-5" : "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4")}>
          {Array.from({ length: kpiCount }).map((_, i) => (
            <Card
              key={i}
              className="min-h-[132px] rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 shadow-none"
            >
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="mt-4 h-8 w-20" />
              <Skeleton className="mt-auto h-4 w-24" />
            </Card>
          ))}
        </div>
      )}

      {variant === "detail" ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className={cn(dashboardStyles.sectionCard, "lg:col-span-2 space-y-3")}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-2xl" />
            ))}
          </Card>
          <Card className={cn(dashboardStyles.sectionCard, "space-y-3")}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-2xl" />
            ))}
          </Card>
        </div>
      ) : variant === "form" ? (
        <Card className={cn(dashboardStyles.sectionCard, "space-y-4")}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-2xl" />
          ))}
        </Card>
      ) : (
        <Card className={cn(dashboardStyles.sectionCard, "space-y-3 p-4 sm:p-5")}>
          <div className="flex gap-3">
            <Skeleton className="h-10 flex-1 rounded-full" />
            <Skeleton className="h-10 w-36 rounded-full" />
          </div>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-2xl" />
          ))}
        </Card>
      )}
    </div>
  );
}
